import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SimpleIndexedDBService } from './simple-indexeddb.service';

@Injectable({
  providedIn: 'root'
})
export class UserProfileStorageService {
  private readonly USER_PROFILE_KEY = '_ionicstorage/_ionickv/userSubject';
  private userProfile: any = null;
  private profileSubject = new BehaviorSubject<any>(null);
  private isLoaded = false;
  private isLoading = false;

  constructor(private indexedDB: SimpleIndexedDBService) {}

  /**
   * Save user profile to IndexedDB
   * @param profile User profile object
   */
  async saveProfile(profile: any): Promise<void> {
    try {
      await this.indexedDB.setItem(this.USER_PROFILE_KEY, profile);
      console.log('User profile saved to IndexedDB');
    } catch (error) {
      console.error('Failed to save user profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile from IndexedDB
   * @returns User profile object or null if not found
   */
  async getProfile(): Promise<any> {
    try {
      const profile = await this.indexedDB.getItem(this.USER_PROFILE_KEY);
      return profile;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      return null;
    }
  }

  /**
   * Remove user profile from IndexedDB
   */
  async removeProfile(): Promise<void> {
    try {
      await this.indexedDB.removeItem(this.USER_PROFILE_KEY);
      console.log('User profile removed from IndexedDB');
    } catch (error) {
      console.error('Failed to remove user profile:', error);
      throw error;
    }
  }

  /**
   * Clear all storage data
   */
  async clearAll(): Promise<void> {
    try {
      await this.indexedDB.clear();
      console.log('All storage data cleared');
    } catch (error) {
      console.error('Failed to clear storage:', error);
      throw error;
    }
  }

  /**
   * Check if user profile exists
   * @returns true if profile exists, false otherwise
   */
  async hasProfile(): Promise<boolean> {
    try {
      const profile = await this.getProfile();
      return profile !== null && profile !== undefined;
    } catch (error) {
      console.error('Failed to check profile existence:', error);
      return false;
    }
  }

  /**
   * Get specific property from user profile (async version)
   * @param propertyName Property name to retrieve
   * @returns Property value or null if not found
   */
  async getProfilePropertyAsync(propertyName: string): Promise<any> {
    try {
      const profile = await this.getProfile();
      return profile && profile[propertyName] ? profile[propertyName] : null;
    } catch (error) {
      console.error(`Failed to get profile property ${propertyName}:`, error);
      return null;
    }
  }

  /**
   * Update specific property in user profile
   * @param propertyName Property name to update
   * @param value New value for the property
   */
  async updateProfileProperty(propertyName: string, value: any): Promise<void> {
    try {
      const profile = await this.getProfile();
      if (profile) {
        profile[propertyName] = value;
        await this.saveProfile(profile);
        console.log(`Profile property ${propertyName} updated`);
      } else {
        throw new Error('No profile found to update');
      }
    } catch (error) {
      console.error(`Failed to update profile property ${propertyName}:`, error);
      throw error;
    }
  }


  /**
   * Get storage information for debugging
   */
  async getStorageInfo(): Promise<any> {
    try {
      const dbInfo = await this.indexedDB.getDBInfo();
      const hasProfile = await this.hasProfile();
      const profile = await this.getProfile();
      
      return {
        ...dbInfo,
        hasUserProfile: hasProfile,
        profileKeys: profile ? Object.keys(profile) : [],
        userProfileKey: this.USER_PROFILE_KEY
      };
    } catch (error) {
      return {
        error: error.message,
        userProfileKey: this.USER_PROFILE_KEY
      };
    }
  }

  /**
   * Initialize profile on app startup - loads once and caches
   */
  async initializeProfile(): Promise<void> {
    if (this.isLoaded || this.isLoading) {
      return;
    }

    this.isLoading = true;
    try {
      // Get profile directly from IndexedDB
      const profile = await this.indexedDB.getItem(this.USER_PROFILE_KEY);

      this.userProfile = profile;
      this.profileSubject.next(profile);
      this.isLoaded = true;
      
      console.log('User profile initialized:', profile ? 'Profile loaded' : 'No profile found');
    } catch (error) {
      console.error('Failed to initialize user profile:', error);
      this.userProfile = null;
      this.profileSubject.next(null);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get cached profile synchronously (must call initializeProfile first)
   */
  get profile(): any {
    return this.userProfile;
  }

  /**
   * Get profile as Observable for reactive components
   */
  get profile$(): Observable<any> {
    return this.profileSubject.asObservable();
  }

  /**
   * Update profile and notify all subscribers
   */
  async updateProfile(profile: any): Promise<void> {
    try {
      await this.indexedDB.setItem(this.USER_PROFILE_KEY, profile);
      this.userProfile = profile;
      this.profileSubject.next(profile);
      console.log('User profile updated and cached');
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw error;
    }
  }

  /**
   * Clear profile and notify all subscribers
   */
  async clearProfile(): Promise<void> {
    try {
      await this.indexedDB.clear();
      this.userProfile = null;
      this.profileSubject.next(null);
      this.isLoaded = false;
      console.log('User profile cleared');
    } catch (error) {
      console.error('Failed to clear user profile:', error);
      throw error;
    }
  }

  /**
   * Check if profile is loaded and available
   */
  get isProfileLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Check if profile is currently loading
   */
  get isProfileLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Get specific property from cached profile
   */
  getProfileProperty(propertyName: string): any {
    return this.userProfile && this.userProfile[propertyName] ? this.userProfile[propertyName] : null;
  }

  /**
   * Check if user has specific roles
   */
  hasRoles(roles: number[]): boolean {
    if (!this.userProfile || !this.userProfile.roles) {
      return false;
    }
    
    for (const aRole of this.userProfile.roles) {
      if (roles.includes(aRole)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get user's agency ID
   */
  get agencyId(): number | null {
    return this.getProfileProperty('agency');
  }

  /**
   * Get user's roles
   */
  get roles(): number[] {
    return this.getProfileProperty('roles') || [];
  }

  /**
   * Synchronous getter for backward compatibility
   * @deprecated Use the new 'profile' getter instead
   */
  getProfileSync(): any {
    return this.userProfile;
  }
}
